// Opt-in trace of the smart-navigation path (#1311). Off unless someone sets the flag, so it costs
// one boolean check in production.
//
// Why an in-page ring buffer rather than plain console.log: the interesting part of this feature is
// the ORDER of a dozen events across two panes inside a few hundred milliseconds — a scroll issued
// by the user's own click, the plan that follows it, the session it starts, the counterpart
// selection, and every settle pass of two independent scroll loops. Scraping that out of the console
// is slow and lossy; reading one JSON array in a single browser_evaluate is not.
//
// From the console, two calls: arm it, do the interaction, save it.
//   __ASQ_SMARTNAV_START__()   // trace on + buffer cleared
//   __ASQ_SMARTNAV_SAVE__()    // downloads smartnav-log.json
// Or read it in place (what Playwright does): `window.__ASQ_SMARTNAV_LOG__`, and set
// `window.__ASQ_SMARTNAV_ECHO__ = true` to also print each entry as it happens.

const MAX_ENTRIES = 400
const SAVE_FILENAME = "smartnav-log.json"

type TDebugWindow = typeof window & {
	__ASQ_SMARTNAV_DEBUG__?: boolean
	__ASQ_SMARTNAV_LOG__?: Record<string, unknown>[]
	__ASQ_SMARTNAV_ECHO__?: boolean
	__ASQ_SMARTNAV_START__?: () => string
	__ASQ_SMARTNAV_SAVE__?: () => string
}

export const isSmartNavDebugOn = (): boolean =>
	typeof window !== "undefined" && (window as TDebugWindow).__ASQ_SMARTNAV_DEBUG__ === true

// `t` is relative to the first entry, which is what makes an ordering bug readable at a glance.
export const smartNavDebug = (event: string, data?: Record<string, unknown>): void => {
	if (!isSmartNavDebugOn()) return
	const w = window as TDebugWindow
	const log = (w.__ASQ_SMARTNAV_LOG__ ??= [])
	const now = performance.now()
	const t = log.length === 0 ? 0 : Math.round(now - (log[0].__now as number))
	const entry = { t, event, ...data, __now: now }
	log.push(entry)
	if (log.length > MAX_ENTRIES) log.splice(0, log.length - MAX_ENTRIES)
	// Opt-in echo, for watching it live rather than reading the buffer afterwards.
	if (w.__ASQ_SMARTNAV_ECHO__ === true) console.log(`[smartnav +${t}ms] ${event}`, data ?? "")
}

// Console helpers, attached on import so no build flag or restart is needed to reach them.
//
// `__ASQ_SMARTNAV_SAVE__` downloads the buffer as a FILE rather than putting it on the clipboard:
// some browsers (Arc among them) refuse `copy()` from the console, and a console dump of a 40-entry
// array is not something anyone should have to transcribe. A blob download needs no permission.
if (typeof window !== "undefined") {
	const w = window as TDebugWindow

	w.__ASQ_SMARTNAV_START__ = () => {
		w.__ASQ_SMARTNAV_DEBUG__ = true
		w.__ASQ_SMARTNAV_LOG__ = []
		return "smartnav trace ON and cleared — do the interaction, then run __ASQ_SMARTNAV_SAVE__()"
	}

	w.__ASQ_SMARTNAV_SAVE__ = () => {
		const log = w.__ASQ_SMARTNAV_LOG__ ?? []
		// Drop the internal high-resolution stamp; `t` (ms since the first entry) is the readable one.
		const body = JSON.stringify(
			log.map((entry) => {
				const { __now, ...rest } = entry
				void __now
				return rest
			}),
			null,
			1,
		)
		const url = URL.createObjectURL(new Blob([body], { type: "application/json" }))
		const link = document.createElement("a")
		link.href = url
		link.download = SAVE_FILENAME
		document.body.appendChild(link)
		link.click()
		link.remove()
		window.setTimeout(() => URL.revokeObjectURL(url), 1000)
		return `saved ${log.length} entries to ${SAVE_FILENAME}`
	}
}
